import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { clearBootstrapTokenForSource } from "../auth/bootstrapTokens";
import "../styles/pages/AuthRecovery.css";
import API_BASE from "../api";

/**
 * Reads the Supabase recovery token and type directly from window.location.hash.
 * Called inside a useState initializer so it runs synchronously on first render,
 * before any useEffect in the tree can sanitize the URL.
 */
function readRecoveryTokenFromHash() {
  if (typeof window === "undefined") {
    return { token: null, isRecovery: false };
  }

  const raw = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash;

  if (!raw) {
    return { token: null, isRecovery: false };
  }

  const params = new URLSearchParams(raw);
  const token = (params.get("access_token") || "").trim();
  const type = (params.get("type") || "").trim();

  return {
    token: token || null,
    isRecovery: type === "recovery",
  };
}

export default function ResetPassword() {
  const { t } = useTranslation();

  // Capture token synchronously before any effects sanitize the URL hash.
  const [recovery] = useState(() => readRecoveryTokenFromHash());

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // null | "success" | "expired" | "error"
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Clean the raw token out of the URL bar.
    if (window.location.hash) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // Clear any recovery token the session bootstrap may have stored in
    // sessionStorage so it cannot be used as a regular session token later.
    clearBootstrapTokenForSource("supabase");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!newPassword) {
      alert(
        t("reset_password_error_required", {
          defaultValue: "Please enter a new password.",
        })
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      alert(
        t("passwords_no_match", { defaultValue: "Passwords do not match." })
      );
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("token", recovery.token);
      formData.append("new_password", newPassword);

      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        let detail = "";
        try {
          const body = await res.json();
          detail = body?.detail || "";
        } catch {
          detail = "";
        }

        setIsLoading(false);
        if (detail === "reset_token_invalid") {
          setResult("expired");
        } else {
          setResult("error");
        }
        return;
      }

      setIsLoading(false);
      setResult("success");
    } catch (err) {
      setIsLoading(false);
      console.error("Reset password error:", err);
      setResult("error");
    }
  };

  // ── No valid recovery token ──────────────────────────────────────────────
  if (!recovery.token || !recovery.isRecovery) {
    return (
      <div className="auth-recovery-page">
        <div className="auth-recovery-card">
          <h2 className="auth-recovery-title">
            {t("reset_password_invalid_title", {
              defaultValue: "Invalid reset link",
            })}
          </h2>
          <p className="auth-recovery-text">
            {t("reset_password_invalid_message", {
              defaultValue:
                "This password reset link is invalid or has already been used.",
            })}
          </p>
          <p className="auth-recovery-footer">
            <Link to="/forgot-password" className="auth-recovery-link">
              {t("reset_password_request_new", {
                defaultValue: "Request a new reset link",
              })}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // ── Success ──────────────────────────────────────────────────────────────
  if (result === "success") {
    return (
      <div className="auth-recovery-page">
        <div className="auth-recovery-card">
          <h2 className="auth-recovery-title">
            {t("reset_password_success_title", {
              defaultValue: "Password updated",
            })}
          </h2>
          <p className="auth-recovery-text">
            {t("reset_password_success_message", {
              defaultValue:
                "Your password has been updated. You can now log in with your new password.",
            })}
          </p>
          <p className="auth-recovery-footer">
            <Link to="/login" className="auth-recovery-link">
              {t("back_to_login", { defaultValue: "Back to login" })}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // ── Token expired ─────────────────────────────────────────────────────────
  if (result === "expired") {
    return (
      <div className="auth-recovery-page">
        <div className="auth-recovery-card">
          <h2 className="auth-recovery-title">
            {t("reset_password_expired_title", { defaultValue: "Link expired" })}
          </h2>
          <p className="auth-recovery-text">
            {t("reset_password_expired_message", {
              defaultValue:
                "This reset link has expired. Please request a new one.",
            })}
          </p>
          <p className="auth-recovery-footer">
            <Link to="/forgot-password" className="auth-recovery-link">
              {t("reset_password_request_new", {
                defaultValue: "Request a new reset link",
              })}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // ── Unexpected error ──────────────────────────────────────────────────────
  if (result === "error") {
    return (
      <div className="auth-recovery-page">
        <div className="auth-recovery-card">
          <h2 className="auth-recovery-title">
            {t("reset_password_error_title", {
              defaultValue: "Something went wrong",
            })}
          </h2>
          <p className="auth-recovery-text">
            {t("reset_password_error_message", {
              defaultValue: "Unable to update your password. Please try again.",
            })}
          </p>
          <button
            type="button"
            className="auth-recovery-btn"
            onClick={() => setResult(null)}
          >
            {t("try_again", { defaultValue: "Try again" })}
          </button>
          <p className="auth-recovery-footer">
            <Link to="/forgot-password" className="auth-recovery-link">
              {t("reset_password_request_new", {
                defaultValue: "Request a new reset link",
              })}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // ── Password form ─────────────────────────────────────────────────────────
  return (
    <div className="auth-recovery-page">
      <div className="auth-recovery-card">
        <h2 className="auth-recovery-title">
          {t("reset_password_title", { defaultValue: "Set a new password" })}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="auth-recovery-password-wrapper">
            <label htmlFor="reset-new-password" className="sr-only">
              {t("new_password", { defaultValue: "New password" })}
            </label>
            <input
              id="reset-new-password"
              name="new-password"
              autoComplete="new-password"
              type={showPassword ? "text" : "password"}
              placeholder={t("new_password", {
                defaultValue: "New password",
              })}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="auth-recovery-input"
            />
            <button
              type="button"
              className="auth-recovery-toggle-btn"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? t("hide") : t("show")}
            </button>
          </div>

          <label htmlFor="reset-confirm-password" className="sr-only">
            {t("confirm_password", { defaultValue: "Confirm new password" })}
          </label>
          <input
            id="reset-confirm-password"
            name="confirm-password"
            autoComplete="new-password"
            type={showPassword ? "text" : "password"}
            placeholder={t("confirm_password", {
              defaultValue: "Confirm new password",
            })}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="auth-recovery-input"
          />

          <button
            type="submit"
            className="auth-recovery-btn"
            disabled={isLoading}
          >
            {isLoading
              ? t("reset_password_loading", { defaultValue: "Updating…" })
              : t("reset_password_submit", { defaultValue: "Update password" })}
          </button>
        </form>
      </div>
    </div>
  );
}
